import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { getQueryParam } from '../../routing/getQueryParam';
import { getIsNewUser, getIsVerifiedByGoogleCredential, getVerificationType } from '../../store/app/selectors';
import { arePreAccountCreatedExperimentsFetched } from '../../store/experiments/selectors';
import { EventName } from '../../tracking/EventName';
import { getTracker } from '../../tracking/getTracker';
import { useTracker } from '../../tracking/useTracker';
import { useTrackSignupInitiated } from '../../tracking/useTrackSignupInitiated';
import { getGooglePromptQueryParameterValue, getIsEnrolledForGooglePromptExperiment, GOOGLE_PROMPT_EXPERIMENT_TREATMENT_QUERY_PARAM, PROMPT_CLICKED_QUERY_PARAM, PROMPT_DISMISSED_QUERY_PARAM, PROMPT_NOT_CLICKED_QUERY_PARAM } from './googlePromptUtils';
import { setVerificationType } from '../../store/app/action-creators';
import { VerificationType } from '../../app/VerificationType';
import { ExperimentActionTypes } from '../../store/experiments/action-types';
import { getUserExperimentIdentifier } from '../../experiments/getUserExperimentIdentifier';
import { ExperimentIdentifier } from '../../experiments/ExperimentIdentifier';
export const GOOGLE_PROMPT_EXPERIMENT_EXPERIMENT_ID = null;
let hasExposureBeenTrackedForGooglePromptExperimentCache = false;
let hasTrackedPromptInteractions = false;
export function setHasTrackedPromptInteractions(value) {
  hasTrackedPromptInteractions = value;
}
export function getHasExposureBeenTrackedForGooglePromptExperiment() {
  return hasExposureBeenTrackedForGooglePromptExperimentCache;
}
export function resetExposureForGooglePromptExperiment() {
  hasExposureBeenTrackedForGooglePromptExperimentCache = false;
}

function setHasExposureBeenTrackedForGooglePromptExperiment() {
  hasExposureBeenTrackedForGooglePromptExperimentCache = true;
}

const updateGooglePromptExperimentInState = () => dispatch => {
  if (!GOOGLE_PROMPT_EXPERIMENT_EXPERIMENT_ID) {
    return;
  }

  const googlePromptExperimentKey = GOOGLE_PROMPT_EXPERIMENT_EXPERIMENT_ID;
  const experimentIdentifier = getUserExperimentIdentifier();
  const googlePromptExperimentParameter = getQueryParam(GOOGLE_PROMPT_EXPERIMENT_TREATMENT_QUERY_PARAM);
  dispatch({
    type: ExperimentActionTypes.BulkUpdateExperiments,
    response: {
      [googlePromptExperimentKey]: {
        key: GOOGLE_PROMPT_EXPERIMENT_EXPERIMENT_ID,
        identifier: experimentIdentifier,
        status: {
          isActive: true,
          isEnrolled: true,
          isDefaulted: false,
          isCached: false
        },
        parameters: {
          group: googlePromptExperimentParameter
        },
        maxAge: 0,
        trackedExposure: true
      }
    },
    identifierPhase: ExperimentIdentifier.Utk
  });
};

export const trackGooglePromptExperimentExposure = () => {
  const googlePromptExperimentTreatment = getQueryParam(GOOGLE_PROMPT_EXPERIMENT_TREATMENT_QUERY_PARAM);
  setHasExposureBeenTrackedForGooglePromptExperiment();
  getTracker().track(EventName.ExperimentExposure, {
    action: `${GOOGLE_PROMPT_EXPERIMENT_EXPERIMENT_ID}-group-${googlePromptExperimentTreatment}`
  }, true);
};
export const useTrackGooglePromptUsedPreSignup = () => {
  const dispatch = useAppDispatch();
  const isNewUser = useSelector(getIsNewUser);
  const isVerifiedByGoogleCredential = useSelector(getIsVerifiedByGoogleCredential);
  const verificationType = useSelector(getVerificationType);
  const Tracker = useTracker();
  const isEnrolledForGooglePromptExperiment = getIsEnrolledForGooglePromptExperiment();
  const exposureHasBeenTracked = getHasExposureBeenTrackedForGooglePromptExperiment();
  const experimentsFetched = useSelector(arePreAccountCreatedExperimentsFetched);
  useTrackSignupInitiated(isNewUser && isVerifiedByGoogleCredential);
  useEffect(() => {
    if (isEnrolledForGooglePromptExperiment && experimentsFetched && !exposureHasBeenTracked) {
      trackGooglePromptExperimentExposure();
      dispatch(updateGooglePromptExperimentInState());
      setHasExposureBeenTrackedForGooglePromptExperiment();
    }

    if (!hasTrackedPromptInteractions) {
      const promptDismissedValue = getGooglePromptQueryParameterValue(PROMPT_DISMISSED_QUERY_PARAM);
      const promptNotClickedValue = getGooglePromptQueryParameterValue(PROMPT_NOT_CLICKED_QUERY_PARAM);

      if (!isNaN(promptDismissedValue)) {
        Tracker.signupInteraction('prompt-dismissed');
      }

      if (!isNaN(promptNotClickedValue)) {
        Tracker.signupInteraction('prompt-not-clicked');
      }

      setHasTrackedPromptInteractions(true);
    }

    if (isVerifiedByGoogleCredential && verificationType === VerificationType.Google) {
      dispatch(setVerificationType(VerificationType.GooglePrompt));
      const promptClickedValue = getGooglePromptQueryParameterValue(PROMPT_CLICKED_QUERY_PARAM);

      if (!isNaN(promptClickedValue)) {
        Tracker.signupInteraction('prompt-clicked');
      }
    }
  }, [isVerifiedByGoogleCredential, verificationType, experimentsFetched, exposureHasBeenTracked, isEnrolledForGooglePromptExperiment, Tracker, dispatch]);
};